import {
    type BTFileSystem,
    type FileViewOptions,
} from "legacyComponents/FileUploadContainer.types";
import { useContext, useState } from "react";

import { BTButton } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import { BdsIconHomeowners, BdsIconSubsVendors } from "@buildertrend/components";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { CommonConstants } from "types/constants";
import { ImageResizeMode } from "types/enum";

import { BTCard } from "commonComponents/btWrappers/BTCard/BTCard";
import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { MediaContextMenu } from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu";
import {
    areContextItemsPresent,
    showClientViewingPermissions,
    showSubViewingPermissions,
} from "commonComponents/entity/media/common/MediaContextMenu/MediaContextMenu.utility";
import { DragAndDropHandle } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { IconFile } from "commonComponents/utilities/IconFile/IconFile";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import {
    canClickFile,
    canMultiSelectFiles,
    getDocumentThumbnailUrl,
    getLightboxThumbnailUrl,
} from "entity/media/common/media.utils";

import "./MediaAttachmentTile.less";

interface IMediatAttachmentFileContextMenuProps {
    renderOverlay: (menuItemClick: () => void) => JSX.Element;
    items: Record<string, boolean>;
}

interface IMediaAttachmentFileProps {
    file: BTFileSystem;
    entityId: number;
    fileViewOptions?: FileViewOptions;
    contextMenuProps?: IMediatAttachmentFileContextMenuProps;
    onFileClick?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
    isSelected?: boolean;
    onSelectedChanged?: (fileId: number, isSelected: boolean) => void;
    isOnExternalPage?: boolean;
    isReadOnly?: boolean;
    showDragHandle?: boolean;
}

// 100px file container with 16px margin between
export const MAX_ATTACHMENT_TILE_WIDTH = 116;

const thumbnailContainerSize = 100; // Corresponds to @thumbnail-outer-size in Less
const cardBorderWidth = 1; // Standard Ant Design card border width
const thumbnailImageSize = thumbnailContainerSize - 2 * cardBorderWidth;

export const MediaAttachmentTile = ({
    file,
    entityId,
    fileViewOptions,
    onFileClick,
    contextMenuProps,
    isSelected = false,
    isOnExternalPage = false,
    isReadOnly = false,
    onSelectedChanged,
    showDragHandle = false,
}: IMediaAttachmentFileProps) => {
    const userInfo = useContext(UserInfoContext);
    const [isHovering, setIsHovering] = useState(false);

    const canClick = canClickFile(file, entityId);
    const canSelect =
        onSelectedChanged && canMultiSelectFiles([file], isOnExternalPage, isReadOnly);

    const mediaThumbnail = getLightboxThumbnailUrl(file) ?? getDocumentThumbnailUrl(file);

    const clientsText = "Clients can view";
    const subsText = file.viewingPermissions?.showAllSubs
        ? `${CommonConstants.Sub.plural} can view`
        : `Some ${CommonConstants.Sub.plural.toLocaleLowerCase()} can view`;

    const showClientIcon =
        file.viewingPermissions?.showOwner &&
        showClientViewingPermissions(file, false, fileViewOptions, userInfo?.loginType);

    const showSubIcon =
        file.viewingPermissions?.showSubs &&
        showSubViewingPermissions(file, false, fileViewOptions, userInfo?.loginType);

    const handleSelectedChanged = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectedChanged?.(file.id, !isSelected);
    };

    const handleThumbnailCheckboxContainerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const cardCover = (
        <div
            data-testid={`ThumbnailContainer-${file.id}`}
            className={cn("ThumbnailContainer", {
                ClickableThumbnail: canClick,
            })}
            onClick={canClick ? onFileClick : undefined}
            onMouseEnter={canSelect ? () => setIsHovering(true) : undefined}
            onMouseLeave={canSelect ? () => setIsHovering(false) : undefined}
        >
            {showDragHandle && (
                <div className="TileDragHandle">
                    <DragAndDropHandle />
                </div>
            )}
            <IconFile
                className="TileThumbnail"
                isFolder={false}
                isGlobalFolder={false}
                fileType={file.extension}
                size="xlarge"
                thumbnail={mediaThumbnail}
                thumbnailSize={thumbnailImageSize}
                thumbnailResizeMode={ImageResizeMode.Crop}
            />
            {canSelect && (
                <div
                    className="ThumbnailCheckboxContainer"
                    onClick={handleThumbnailCheckboxContainerClick}
                >
                    <BTCheckbox
                        id={`ThumbnailSelectCheckbox-${file.id}`}
                        data-testid={`ThumbnailSelectCheckbox-${file.id}`}
                        className={cn({
                            ThumbnailCheckboxHidden: !isHovering && !isSelected,
                        })}
                        checked={isSelected}
                        onClick={handleSelectedChanged}
                    />
                </div>
            )}
        </div>
    );

    const component = (
        <div
            className="MediaAttachmentTile"
            data-testid={`MediaAttachmentTile-${file.id}`}
            data-file-id={file.id}
            style={{ maxWidth: MAX_ATTACHMENT_TILE_WIDTH }}
        >
            <BTCard bodyStyle={{ padding: 0 }} cover={cardCover}>
                <div className="flex padding-horizontal-xs padding-vertical-xss Footer">
                    <div className="FileInfo">
                        <div className="FileTitleWrapper">
                            {canClick ? (
                                <BTButton
                                    data-testid={`FileTitleContainer-${file.id}`}
                                    type="tertiary"
                                    onClick={onFileClick}
                                    className="ClickableFileTitle"
                                >
                                    <TextTruncate
                                        className={
                                            contextMenuProps ? "FileTitle" : "FileTitleNoContext"
                                        }
                                    >
                                        {file.title}
                                    </TextTruncate>
                                </BTButton>
                            ) : (
                                <TextTruncate
                                    className={
                                        contextMenuProps ? "FileTitle" : "FileTitleNoContext"
                                    }
                                >
                                    {file.title}
                                </TextTruncate>
                            )}
                        </div>
                        <div className="FileViewingPermissions flex padding-top-xss">
                            {showClientIcon && (
                                <BTPopover
                                    id={`file-permissions-clients-tooltip-${file.id}`}
                                    content={clientsText}
                                    data-testid={`file-permissions-clients-tooltip-${file.id}`}
                                    widthType="unrestricted"
                                >
                                    <div
                                        className="margin-right-xs"
                                        data-testid={`file-permissions-clients-icon-${file.id}`}
                                    >
                                        <BdsIconHomeowners size="sm" />
                                    </div>
                                </BTPopover>
                            )}
                            {showSubIcon && (
                                <BTPopover
                                    id={`file-permissions-subs-tooltip-${file.id}`}
                                    content={subsText}
                                    data-testid={`file-permissions-subs-tooltip-${file.id}`}
                                    widthType="unrestricted"
                                >
                                    <div
                                        className="margin-right-xs"
                                        data-testid={`file-permissions-subs-icon-${file.id}`}
                                    >
                                        <BdsIconSubsVendors size="sm" />
                                    </div>
                                </BTPopover>
                            )}
                        </div>
                    </div>
                    {contextMenuProps && areContextItemsPresent(contextMenuProps.items) && (
                        <div className="AttachmentTileContextMenu">
                            <MediaContextMenu
                                renderOverlay={contextMenuProps?.renderOverlay}
                                file={file}
                            />
                        </div>
                    )}
                </div>
            </BTCard>
        </div>
    );

    if (contextMenuProps !== undefined && areContextItemsPresent(contextMenuProps.items)) {
        return (
            <MediaContextMenu renderOverlay={contextMenuProps.renderOverlay} file={file}>
                {component}
            </MediaContextMenu>
        );
    }

    return component;
};
